import {
  createFileRoute,
  useNavigate,
  Link,
  Outlet,
  useChildMatches,
} from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUOTE_TERMS_FIELDS, fetchCompanyQuoteTerms } from "@/lib/quote-terms";
import { PageHeader } from "@/components/app-shell";
import { CreateInvoiceDialog } from "@/components/invoices/CreateInvoiceDialog";
import { shouldShowInvoiceAction } from "@/lib/invoice-from-quote";
import { nextQuoteAction } from "@/lib/quote-next-action";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { usePricingRole } from "@/hooks/use-pricing-role";
import { useActiveCompany } from "@/hooks/use-active-company";
import { ArrowLeft, Plus, Trash2, Package, Clock, Truck, FileText, Printer, Save, EyeOff, Eye, Search, AlertTriangle, MessageSquare, GitBranch, Zap, Percent, Wrench, Sparkles, Copy, ArrowUp, ArrowDown, LayoutTemplate, Loader2, CheckCircle2, XCircle, History, Lock, Send, ReceiptText } from "lucide-react";
import { SendQuoteDialog } from "@/components/quotes/SendQuoteDialog";
import { AddStandardJobModal } from "@/components/standard-jobs/AddStandardJobModal";
import { MaterialPicker as RegistryMaterialPicker } from "@/components/calculators/MaterialPicker";
import type { MaterialRow } from "@/hooks/use-materials-search";
import type { ModuleKey } from "@/modules";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { formatCurrency, formatQuantity } from "@/lib/format";
import {
  computeQuoteTotals, computeSalePrice, resolveMarkup,
  marginColorClass, evaluateMargin,
  collapseForCustomer, DISPLAY_MODE_LABELS, PROJECT_TYPES,
  QUOTE_STATUS_LABELS, QUOTE_STATUS_CLASSES, statusTimestampColumn, validateReadyToSend,
  type MaterialDisplayMode, type ProjectType, type QuoteStatus,
} from "@/lib/calc";
import { logQuoteActivity, type QuoteActivityKind } from "@/lib/quote-activity";
import { assertTransition, allowedNextStatuses, isLockedStatus } from "@/domain/quote-status";
import { useCompanyProfile } from "@/hooks/use-company-profile";
import { QuoteSectionsPanel, useQuoteSections } from "@/components/quotes/QuoteSectionsPanel";
import { CopyQuoteDialog } from "@/components/quotes/CopyQuoteDialog";
import { QuoteTemplateDialog, type TemplateAppliedLine } from "@/components/quotes/QuoteTemplateDialog";

export const Route = createFileRoute("/_authenticated/quotes/$id")({
  component: QuoteEditor,
});

type Item = {
  id?: string;
  item_type: "material" | "labor" | "service_vehicle" | "documentation" | "lift" | "travel" | "option" | "small_parts" | "discount";
  material_id: string | null;
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  unit_price: number;
  is_hidden_on_quote: boolean;
  sort_order: number;
  internal_note: string | null;
  customer_note: string | null;
  labor_rate_id: string | null;
  /** Bolk A2: hvilken post linja tilhører. null = implisitt «Diverse». */
  section_id: string | null;
};


type Quote = {
  id: string;
  quote_number: string;
  title: string;
  description: string | null;
  customer_id: string | null;
  status: QuoteStatus;
  valid_until: string | null;
  vat_percent: number;
  discount_percent: number;
  notes: string | null;
  project_type: ProjectType;
  material_display_mode: MaterialDisplayMode;
  presentation: "fixed_price" | "itemized";
  revision_of: string | null;
  revision_label: string | null;
  project_number: string | null;
  company_id: string;
  module_key: string;
  project_id: string | null;
  updated_at?: string | null;
  customer_summary: string | null;
  /** Utgått – erstattet av terms_*-feltene under. Skrives ikke lenger til. */
  terms_override: string | null;
  terms_included: string | null;
  terms_excluded: string | null;
  terms_reservations: string | null;
  terms_payment: string | null;
  include_images: boolean;
  included_image_ids: string[] | null;
  use_cover_page: boolean | null;
};

type SaveState = "idle" | "saving" | "saved" | "error";

type ActivityRow = {
  id: string;
  kind: string;
  summary: string;
  created_at: string;
};

type MaterialLite = {
  id: string;
  item_number: string | null;
  description: string;
  unit: string;
  purchase_price: number;
  markup_percent: number | null;
  supplier: string | null;
  manufacturer: string | null;
  is_small_part: boolean;
  is_favorite: boolean;
  category_id: string | null;
  image_url: string | null;
  list_price: number | null;
  customer_price: number | null;
};


type LaborRate = { id: string; name: string; cost_rate: number; sell_rate: number; is_default: boolean };
type CategoryLite = { id: string; name: string };

const TYPE_LABEL: Record<Item["item_type"], string> = {
  material: "Materiell", labor: "Arbeid (timer)", service_vehicle: "Servicebil",
  documentation: "Dokumentasjon", lift: "Lift", travel: "Reise (km)",
  option: "Tilvalg", small_parts: "Smådeler", discount: "Rabatt",
};

const STATUS_KEYS = Object.keys(QUOTE_STATUS_LABELS) as QuoteStatus[];

function nextRevisionLabel(prev: string | null): string {
  if (!prev) return "A";
  const last = prev.charCodeAt(prev.length - 1);
  return String.fromCharCode(last + 1);
}

function QuoteEditor() {
  // Denne ruten er forelder til quotes.$id.assistant. Når assistent-underruten
  // er aktiv må vi rendre <Outlet /> så den vises; ellers ville tilbudsassistenten
  // aldri blitt synlig (samme Outlet-mangel som prosjektsiden hadde). Uten aktiv
  // underrute viser vi tilbudseditoren.
  const childMatches = useChildMatches();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState<"calc" | "quote">("calc");
  const [openNotes, setOpenNotes] = useState<Record<number, boolean>>({});
  const [standardJobOpen, setStandardJobOpen] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [readySendErrors, setReadySendErrors] = useState<string[]>([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  // Bolk 4: internprisvisning. Standard PÅ, men aldri tilgjengelig for ansatt-rollen.
  const [showInternalPref, setShowInternalPref] = useState(true);
  const { data: companyProfile } = useCompanyProfile();
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setUid(data.user?.id ?? null)); }, []);


  function applyStandardJob({ lines, customer_summary }: { lines: { item_type: string; description: string; quantity: number; unit: string; unit_price: number; unit_cost: number; is_hidden_on_quote: boolean; customer_note?: string }[]; customer_summary: string }) {
    setItems((arr) => [
      ...arr,
      ...lines.map((l, i) => ({
        item_type: l.item_type as Item["item_type"],
        material_id: null,
        description: l.description,
        quantity: l.quantity,
        unit: l.unit,
        unit_cost: l.unit_cost,
        unit_price: l.unit_price,
        is_hidden_on_quote: l.is_hidden_on_quote,
        sort_order: arr.length + i,
        internal_note: null,
        customer_note: l.customer_note ?? null,
        labor_rate_id: null,
        section_id: null,
      })),
    ]);
    setQuote((q) => {
      if (!q) return q;
      if (!customer_summary.trim()) return q;
      const existing = (q.description ?? "").trim();
      return {
        ...q,
        description: existing ? `${existing}\n\n${customer_summary}` : customer_summary,
      };
    });
    setDirty(true);
  }

  const { data } = useQuery({
    queryKey: ["quote", id],
    queryFn: async () => {
      const [q, its, cs, ms, st, cm, lr, cats] = await Promise.all([
        supabase.from("quotes").select("*").eq("id", id).single(),
        supabase.from("quote_items").select("*").eq("quote_id", id).order("sort_order"),
        supabase.from("customers").select("id,name,email").order("name"),
        supabase.from("materials").select("id,item_number,description,unit,purchase_price,markup_percent,supplier,manufacturer,is_small_part,is_favorite,category_id,image_url,list_price,customer_price").eq("is_active", true).order("is_favorite", { ascending: false }).order("description").range(0, 19999),
        supabase.from("settings").select("*").eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "").maybeSingle(),
        supabase.from("material_category_markups").select("category_id,markup_percent"),
        supabase.from("labor_rates").select("id,name,cost_rate,sell_rate,is_default").eq("is_active", true).order("sort_order"),
        supabase.from("material_categories").select("id,name").order("sort_order"),
      ]);
      if (q.error) throw q.error;
      const catMap: Record<string, number> = {};
      for (const r of cm.data ?? []) if (r.category_id) catMap[r.category_id] = Number(r.markup_percent);
      return {
        quote: q.data as unknown as Quote,
        items: (its.data ?? []) as unknown as Item[],
        customers: (cs.data ?? []) as { id: string; name: string }[],
        materials: (ms.data ?? []) as unknown as MaterialLite[],
        settings: st.data,
        markupCtx: {
          defaultMarkup: Number(st.data?.default_markup_percent ?? 35),
          categoryMarkups: catMap,
        },
        laborRates: (lr.data ?? []) as LaborRate[],
        categories: (cats.data ?? []) as CategoryLite[],
      };
    },
    // Editoren eier lokal state mens brukeren redigerer. Refetch ved
    // vindusfokus overskrev tidligere usparte endringer.
    refetchOnWindowFocus: false,
  });

  // Lokal state er sannhet så lenge editoren er åpen. Serverdata hydrerer
  // kun ved første last av dette tilbudet (eller ved eksplisitt «Forkast
  // endringer»). Tidligere skrev denne effekten serverdata inn ved hver
  // refetch, som slettet valg og endringer brukeren nettopp gjorde.
  const hydratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data) return;
    if (hydratedIdRef.current === id) return;
    hydratedIdRef.current = id;
    setQuote(data.quote);
    setItems(data.items);
    setDirty(false);
  }, [data, id]);

  /** Forkaster lokale endringer og henter serverversjonen på nytt. */
  function discardLocalChanges() {
    if (!data) return;
    setQuote(data.quote);
    setItems(data.items);
    setDirty(false);
    setSaveState("idle");
  }


  // Aktivitetslogg + revisjonssjekk (skrivebeskyttet hvis noen har opprettet en ny revisjon fra denne)
  const { data: activity } = useQuery({
    queryKey: ["quote-activity", id],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("quote_activity")
        .select("id,kind,summary,created_at")
        .eq("quote_id", id)
        .order("created_at", { ascending: false })
        .limit(50);
      return (rows ?? []) as ActivityRow[];
    },
  });
  const { data: childRevisions } = useQuery({
    queryKey: ["quote-children", id],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("quotes")
        .select("id,quote_number,revision_label")
        .eq("revision_of", id);
      return rows ?? [];
    },
  });
  const projectId = data?.quote.project_id ?? null;
  const { data: projectImages } = useQuery({
    queryKey: ["quote-project-images", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      if (!projectId) return [];
      const { data: rows } = await supabase
        .from("project_images")
        .select("id, storage_path, caption")
        .eq("project_id", projectId)
        .order("created_at");
      const withUrls = await Promise.all(
        (rows ?? []).map(async (r) => {
          const { data: s } = await supabase.storage
            .from("project-images")
            .createSignedUrl(r.storage_path, 60 * 60);
          return { id: r.id, caption: r.caption, url: s?.signedUrl ?? null };
        }),
      );
      return withUrls.filter((x) => x.url) as { id: string; caption: string | null; url: string }[];
    },
  });
  const isSupersededRevision = (childRevisions?.length ?? 0) > 0;
  // Sendt tilbud er låst i databasen. Editoren speiler låsen slik at brukeren
  // ser det med én gang, ikke først når lagring avvises.
  const isLocked = quote ? isLockedStatus(quote.status) : false;
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const { canManagePricing } = usePricingRole();
  const { companyId, activeModuleKey } = useActiveCompany();
  // «Lag faktura» vises fra sendt/låst tilbud og oppover, for roller som kan fakturere.
  // Selve opprettelsen valideres i dialogen (kun godkjent tilbud kan faktureres).
  const canInvoice = Boolean(quote && shouldShowInvoiceAction(quote.status) && canManagePricing);

  const readOnly = isSupersededRevision || isLocked;

  const totals = useMemo(
    () => computeQuoteTotals(items, { discount_percent: quote?.discount_percent, vat_percent: quote?.vat_percent }),
    [items, quote?.discount_percent, quote?.vat_percent],
  );

  // Bolk 4 – rolle: ansatt skal aldri se kostdata.
  const { data: myRole } = useQuery({
    queryKey: ["my-company-role", quote?.company_id],
    enabled: !!quote?.company_id,
    queryFn: async () => {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) return null;
      const { data: row } = await supabase
        .from("company_members")
        .select("role")
        .eq("company_id", quote!.company_id)
        .eq("user_id", userId)
        .maybeSingle();
      return (row?.role as string | undefined) ?? null;
    },
  });
  const canSeeInternal = myRole !== "employee";
  const showInternal = showInternalPref && canSeeInternal;
  /** Linjeindeks som venter på kost fra materiellregisteret. */
  const [costPickIdx, setCostPickIdx] = useState<number | null>(null);

  const materialsById = useMemo(
    () => new Map((data?.materials ?? []).map((m) => [m.id, m])),
    [data?.materials],
  );

  /** Per-linje kalkyletall: veiledende, oppnådd rabatt, kost, kundepris, DB/DG. */
  const calcRows = useMemo(() => {
    return items.map((it) => {
      const m = it.material_id ? materialsById.get(it.material_id) ?? null : null;
      const sign = it.item_type === "discount" ? -1 : 1;
      const qty = Number(it.quantity) || 0;
      const cost = Number(it.unit_cost) || 0;
      const price = Number(it.unit_price) || 0;
      const listPrice = m?.list_price != null && Number(m.list_price) > 0 ? Number(m.list_price) : null;
      const veil = listPrice ?? (cost > 0 ? cost : price);
      const discountPct = listPrice && listPrice > 0 ? (1 - cost / listPrice) * 100 : null;
      const sumSale = sign * qty * price;
      const db = sign * qty * (price - cost);
      const dg = sumSale !== 0 ? (db / sumSale) * 100 : null;
      const registryPrice = m?.customer_price != null ? Number(m.customer_price) : null;
      const overridden = registryPrice != null && Math.abs(registryPrice - price) > 0.009;
      return { veil, discountPct, cost, price, qty, sumSale, db, dg, registryPrice, overridden, sign };
    });
  }, [items, materialsById]);

  // ---- Bolk A2: poster -------------------------------------------------
  const { data: sections = [] } = useQuoteSections(id);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  /** Linjeindekser gruppert per post. Linjer uten post havner i «Diverse». */
  const lineGroups = useMemo(() => {
    const byId = new Map<string, number[]>();
    const loose: number[] = [];
    items.forEach((it, i) => {
      const sid = it.section_id;
      if (sid && sections.some((s) => s.id === sid)) {
        if (!byId.has(sid)) byId.set(sid, []);
        byId.get(sid)!.push(i);
      } else {
        loose.push(i);
      }
    });
    const groups = sections.map((s) => ({
      key: s.id,
      sectionId: s.id as string | null,
      title: s.title,
      indices: byId.get(s.id) ?? [],
    }));
    groups.push({ key: "__misc", sectionId: null, title: "Diverse", indices: loose });
    return groups.map((g) => ({
      ...g,
      sum: g.indices.reduce((acc, i) => acc + (calcRows[i]?.sumSale ?? 0), 0),
    }));
  }, [items, sections, calcRows]);

  const lineCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const g of lineGroups) if (g.sectionId) out[g.sectionId] = g.indices.length;
    return out;
  }, [lineGroups]);

  /** Bolk A3: legger til linjer fra en mal (priser allerede hentet ferskt). */
  function applyTemplateLines(lines: TemplateAppliedLine[]) {
    setItems((arr) => [
      ...arr,
      ...lines.map((l, i) => ({
        item_type: l.item_type as Item["item_type"],
        material_id: l.material_id,
        description: l.description,
        quantity: l.quantity,
        unit: l.unit,
        unit_cost: l.unit_cost,
        unit_price: l.unit_price,
        is_hidden_on_quote: false,
        sort_order: arr.length + i,
        internal_note: null,
        customer_note: null,
        labor_rate_id: l.labor_rate_id,
        section_id: null,
      })),
    ]);
    setDirty(true);
  }

  function assignSection(idx: number, sectionId: string | null) {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, section_id: sectionId } : it)));
    setDirty(true);
  }
  function moveLineTo(from: number, to: number) {
    if (from === to || to < 0 || to >= items.length) return;
    setItems((arr) => {
      const next = [...arr];
      const [x] = next.splice(from, 1);
      next.splice(to, 0, x);
      return next;
    });
    setOpenNotes({});
    setDirty(true);
  }



  const bolk4 = useMemo(() => {
    let sumVeil = 0, sumCost = 0, sumSale = 0, sumLabor = 0;
    calcRows.forEach((r, i) => {
      sumVeil += r.sign * r.qty * r.veil;
      sumCost += r.sign * r.qty * r.cost;
      sumSale += r.sumSale;
      if (items[i]?.item_type === "labor") sumLabor += r.sumSale;
    });
    const db = sumSale - sumCost;
    return {
      sumVeil,
      sumCost,
      sumDiscountAchieved: sumVeil - sumCost,
      sumSale,
      sumLabor,
      db,
      dg: sumSale !== 0 ? (db / sumSale) * 100 : null,
    };
  }, [calcRows, items]);



  const visibleItems = useMemo(() => {
    if (!quote) return [];
    if (mode === "calc") return items.map((it, i) => ({ it, key: String(i), collapsed: false }));
    return collapseForCustomer(items, quote.material_display_mode);
  }, [items, mode, quote]);

  function updateQuote<K extends keyof Quote>(k: K, v: Quote[K]) {
    if (!quote || readOnly) return;
    setQuote({ ...quote, [k]: v });
    setDirty(true);
  }
  function updateItem(idx: number, patch: Partial<Item>) {
    if (readOnly) return;
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  }
  function addItem(type: Item["item_type"]) {
    if (readOnly) return;
    const s = data?.settings;
    const defaultLabor = data?.laborRates.find((r) => r.is_default) ?? data?.laborRates[0];
    const defaults: Partial<Item> = ({
      material: { description: "", unit_cost: 0, unit_price: 0, unit: "stk" },
      labor: {
        description: defaultLabor?.name ?? "Arbeid",
        unit: "time",
        unit_cost: Number(defaultLabor?.cost_rate ?? 0),
        unit_price: Number(defaultLabor?.sell_rate ?? s?.hourly_rate ?? 890),
        labor_rate_id: defaultLabor?.id ?? null,
      },
      service_vehicle: { description: "Servicebil", unit: "stk", unit_cost: 0, unit_price: Number(s?.service_vehicle_rate ?? 750) },
      documentation: { description: "Dokumentasjon / samsvarserklæring", unit: "stk", unit_cost: 0, unit_price: Number(s?.documentation_rate ?? 1500) },
      lift: { description: "Lift", unit: "dag", unit_cost: 0, unit_price: Number(s?.lift_rate ?? 1200) },
      travel: { description: "Reise", unit: "km", unit_cost: 0, unit_price: Number(s?.travel_rate_per_km ?? 8) },
      option: { description: "Tilvalg", unit: "stk", unit_cost: 0, unit_price: 0 },
      small_parts: { description: "Smådeler (WAGO, PN, strips m.m.)", unit: "sett", unit_cost: 0, unit_price: 0 },
      discount: { description: "Rabatt", unit: "stk", unit_cost: 0, unit_price: 0 },
    } as Record<Item["item_type"], Partial<Item>>)[type];
    setItems((arr) => [...arr, {
      item_type: type,
      material_id: null,
      description: defaults.description ?? "",
      quantity: 1,
      unit: defaults.unit ?? "stk",
      unit_cost: defaults.unit_cost ?? 0,
      unit_price: defaults.unit_price ?? 0,
      is_hidden_on_quote: type === "small_parts",
      sort_order: arr.length,
      internal_note: null,
      customer_note: null,
      labor_rate_id: defaults.labor_rate_id ?? null,
      section_id: null,
    }]);
    setDirty(true);
  }
  function addMaterial(materialId: string) {
    const m = data?.materials.find((x) => x.id === materialId);
    if (!m || !data) return;
    const markup = resolveMarkup({
      productMarkup: m.markup_percent,
      categoryId: m.category_id,
      categoryMarkups: data.markupCtx.categoryMarkups,
      defaultMarkup: data.markupCtx.defaultMarkup,
    });
    const price = computeSalePrice(Number(m.purchase_price), markup);
    setItems((arr) => [...arr, {
      item_type: "material",
      material_id: m.id,
      description: m.description,
      quantity: 1,
      unit: m.unit,
      unit_cost: Number(m.purchase_price),
      unit_price: price,
      is_hidden_on_quote: m.is_small_part,
      sort_order: arr.length,
      internal_note: null,
      customer_note: null,
      labor_rate_id: null,
      section_id: null,
    }]);
    setDirty(true);
  }
  /** Fyller inn eksisterende linje med data fra en materialregister-vare. */
  function applyMaterialToLine(idx: number, materialId: string) {
    const m = data?.materials.find((x) => x.id === materialId);
    if (!m || !data) return;
    const markup = resolveMarkup({
      productMarkup: m.markup_percent,
      categoryId: m.category_id,
      categoryMarkups: data.markupCtx.categoryMarkups,
      defaultMarkup: data.markupCtx.defaultMarkup,
    });
    updateItem(idx, {
      material_id: m.id,
      description: m.description,
      unit: m.unit,
      unit_cost: Number(m.purchase_price),
      unit_price: computeSalePrice(Number(m.purchase_price), markup),
      is_hidden_on_quote: m.is_small_part,
    });
  }
  /**
   * Henter kost fra materiellregisteret (leverandørens innkjøpspris) for en
   * linje som mangler kost. Kundeprisen på linja beholdes som den er — vi
   * fyller kun inn kostgrunnlaget slik at DB/DG blir reell.
   */
  function applyCostFromRegistry(idx: number, m: MaterialRow) {
    const cost = Number(m.purchase_price) || 0;
    if (cost <= 0) {
      toast.error("Varen har ingen innkjøpspris i registeret");
      return;
    }
    const cur = items[idx];
    updateItem(idx, {
      material_id: m.id,
      unit_cost: cost,
      unit: cur?.unit && cur.unit.trim() !== "" ? cur.unit : m.unit,
      unit_price: Number(cur?.unit_price) > 0 ? Number(cur.unit_price) : cost,
    });
    toast.success(`Kost hentet fra ${m.supplier ?? "materiellregisteret"}`);
  }

  function removeItem(idx: number) {
    if (readOnly) return;
    setItems((arr) => arr.filter((_, i) => i !== idx));
    setDirty(true);
  }
  function changeLaborRate(idx: number, rateId: string) {
    const r = data?.laborRates.find((x) => x.id === rateId);
    if (!r) return;
    updateItem(idx, {
      labor_rate_id: rateId,
      unit_cost: Number(r.cost_rate),
      unit_price: Number(r.sell_rate),
      description: r.name,
    });
  }

  const save = useMutation({
    mutationFn: async (opts: { isAuto?: boolean } = {}) => {
      if (!quote) return;
      if (readOnly) throw new Error(isLocked ? "Sendt tilbud kan ikke endres. Lag en ny revisjon." : "Skrivebeskyttet – dette tilbudet er en tidligere revisjon.");
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      // Ved manuell lagring med lav DB → be brukeren bekrefte. Autosave hopper over.
      if (!opts.isAuto && quote.status !== "draft" && totals.marginPct < 20 && items.length > 0) {
        if (!confirm(`Dekningsgrad er ${totals.marginPct.toFixed(1)}%. Vil du fortsatt lagre dette tilbudet?`)) {
          throw new Error("Avbrutt av bruker");
        }
      }

      // Statusovergang → sett tidsstempel + evt. prosjektnummer
      const prevStatus = (data?.quote.status ?? "draft") as QuoteStatus;
      const statusChanged = quote.status !== prevStatus;
      if (statusChanged) assertTransition(prevStatus, quote.status);
      const stampCol = statusChanged ? statusTimestampColumn(quote.status) : null;
      const extra: Record<string, string | number | null> = {};
      if (stampCol) extra[stampCol] = new Date().toISOString();

      // Kvalitetssikring før "Klar til sending"
      if (statusChanged && quote.status === "ready_to_send") {
        const errs = validateReadyToSend({
          customer_id: quote.customer_id,
          items_count: items.length,
          total_incl_vat: totals.total,
          company_name: companyProfile?.company_name,
          company_org_number: companyProfile?.org_number,
        });
        if (errs.length > 0) {
          setReadySendErrors(errs);
          throw new Error("Tilbudet er ikke klart til sending. Se sjekklisten.");
        }
        setReadySendErrors([]);
      }

      // Tomme linjer skal aldri lagres – de rendres som blanke 0-kroners rader i kunde-PDF.
      const emptyIdx = items.findIndex((it) => !(it.description ?? "").trim());
      if (emptyIdx >= 0) {
        throw new Error(`Linje ${emptyIdx + 1} mangler beskrivelse. Fyll inn eller slett linjen.`);
      }



      let projectNumber = quote.project_number;
      if (quote.status === "approved" && !projectNumber) {
        const { data: s } = await supabase.from("settings").select("next_project_seq,quote_year").eq("user_id", user.user.id).maybeSingle();
        const year = new Date().getFullYear();
        const seq = s?.quote_year === year ? (s?.next_project_seq ?? 1) : 1;
        projectNumber = `P${year}-${String(seq).padStart(3, "0")}`;
        await supabase.from("settings").update({ next_project_seq: seq + 1 }).eq("user_id", user.user.id);
        extra.project_number = projectNumber;
      }

      const qUpdate = {
        title: quote.title,
        description: quote.description,
        customer_id: quote.customer_id,
        status: quote.status,
        valid_until: quote.valid_until,
        vat_percent: Number(quote.vat_percent),
        discount_percent: Number(quote.discount_percent),
        notes: quote.notes,
        project_type: quote.project_type,
        material_display_mode: quote.material_display_mode,
        presentation: quote.presentation,
        subtotal_excl_vat: totals.subtotal_price,
        vat_amount: totals.vat,
        total_incl_vat: totals.total,
        total_cost: totals.subtotal_cost,
        gross_margin: totals.margin,
        gross_margin_percent: totals.marginPct,
        customer_summary: quote.customer_summary,
        terms_included: quote.terms_included,
        terms_excluded: quote.terms_excluded,
        terms_reservations: quote.terms_reservations,
        terms_payment: quote.terms_payment,
        include_images: quote.include_images,
        included_image_ids: quote.included_image_ids ?? [],
        use_cover_page: quote.use_cover_page,
        ...extra,
      };
      const { error: qErr } = await supabase.from("quotes").update(qUpdate).eq("id", id);
      if (qErr) throw qErr;
      // Granulær upsert: bevar rad-IDer så linje-nivå-sporing (aktivitet, snapshots,
      // eksterne referanser) består. Tidligere delete-and-reinsert nullstilte IDer
      // ved hver lagring.
      const existingIds = new Set((data?.items ?? []).map((it) => it.id).filter((v): v is string => !!v));
      const currentIds = new Set(items.map((it) => it.id).filter((v): v is string => !!v));
      const toDeleteIds = [...existingIds].filter((eid) => !currentIds.has(eid));

      const toRow = (it: Item, i: number) => ({
        quote_id: id,
        user_id: user.user!.id,
        item_type: it.item_type,
        material_id: it.material_id,
        description: it.description,
        quantity: Number(it.quantity),
        unit: it.unit,
        unit_cost: Number(it.unit_cost),
        unit_price: Number(it.unit_price),
        total_cost: Number(it.quantity) * Number(it.unit_cost),
        total_price: Number(it.quantity) * Number(it.unit_price) * (it.item_type === "discount" ? -1 : 1),
        is_hidden_on_quote: it.is_hidden_on_quote,
        sort_order: i,
        internal_note: it.internal_note,
        customer_note: it.customer_note,
        labor_rate_id: it.labor_rate_id,
        section_id: it.section_id,
      });

      if (toDeleteIds.length > 0) {
        const { error: dErr } = await supabase.from("quote_items").delete().in("id", toDeleteIds);
        if (dErr) throw dErr;
      }

      const updates = items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => !!it.id);
      const inserts = items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => !it.id);

      for (const { it, i } of updates) {
        const { error: uErr } = await supabase
          .from("quote_items")
          .update(toRow(it, i) as never)
          .eq("id", it.id!);
        if (uErr) throw uErr;
      }

      if (inserts.length > 0) {
        const payload = inserts.map(({ it, i }) => toRow(it, i));
        const { error: iErr } = await supabase.from("quote_items").insert(payload as never);
        if (iErr) throw iErr;
      }

      // Aktivitetslogg
      if (statusChanged && quote.company_id) {
        await logQuoteActivity({
          quoteId: id,
          companyId: quote.company_id,
          kind: "status_changed",
          summary: `Status endret: ${QUOTE_STATUS_LABELS[prevStatus] ?? prevStatus} → ${QUOTE_STATUS_LABELS[quote.status]}`,
          details: { from: prevStatus, to: quote.status },
        });
      } else if (!opts.isAuto && quote.company_id) {
        await logQuoteActivity({
          quoteId: id,
          companyId: quote.company_id,
          kind: "edited",
          summary: "Tilbud redigert",
        });
      }
    },
    onMutate: () => setSaveState("saving"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quote", id] });
      qc.invalidateQueries({ queryKey: ["quote-activity", id] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setDirty(false);
      setSaveState("saved");
      setLastSavedAt(new Date());
    },
    onError: (e: Error) => {
      setSaveState("error");
      if (e.message !== "Avbrutt av bruker") toast.error(e.message);
    },
  });

  // Autosave: 1,5 s debounce etter siste endring. Hopper over hvis skrivebeskyttet
  // eller en manuell lagring er i gang.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!dirty || readOnly || !quote) return;
    const handle = setTimeout(() => {
      if (!saveRef.current.isPending) saveRef.current.mutate({ isAuto: true });
    }, 1500);
    return () => clearTimeout(handle);
  }, [dirty, readOnly, quote, items]);

  const revise = useMutation({
    mutationFn: async () => {
      if (!quote) throw new Error("Tilbudet er ikke lastet");
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      const rootId = quote.revision_of ?? quote.id;
      const { data: siblings } = await supabase
        .from("quotes")
        .select("revision_label")
        .or(`id.eq.${rootId},revision_of.eq.${rootId}`);
      const latestLabel = (siblings ?? [])
        .map((r) => r.revision_label)
        .filter((x): x is string => !!x)
        .sort()
        .at(-1) ?? null;
      const nextLabel = nextRevisionLabel(latestLabel);

      const { data: newQ, error: qErr } = await supabase.from("quotes").insert({
        user_id: user.user.id,
        company_id: quote.company_id,
        module_key: (quote.module_key ?? "electrician") as "electrician",
        quote_number: quote.quote_number,
        title: quote.title,
        description: quote.description,
        customer_id: quote.customer_id,
        status: "draft",
        valid_until: quote.valid_until,
        vat_percent: Number(quote.vat_percent),
        discount_percent: Number(quote.discount_percent),
        notes: quote.notes,
        project_type: quote.project_type,
        material_display_mode: quote.material_display_mode,
        revision_of: rootId,
        revision_label: nextLabel,
      }).select().single();
      if (qErr) throw qErr;

      if (items.length > 0) {
        const payload = items.map((it, i) => ({
          quote_id: newQ.id,
          user_id: user.user!.id,
          item_type: it.item_type,
          material_id: it.material_id,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_cost: Number(it.unit_cost),
          unit_price: Number(it.unit_price),
          total_cost: Number(it.quantity) * Number(it.unit_cost),
          total_price: Number(it.quantity) * Number(it.unit_price) * (it.item_type === "discount" ? -1 : 1),
          is_hidden_on_quote: it.is_hidden_on_quote,
          sort_order: i,
          internal_note: it.internal_note,
          customer_note: it.customer_note,
          labor_rate_id: it.labor_rate_id,
        }));
        await supabase.from("quote_items").insert(payload as never);
      }

      await logQuoteActivity({
        quoteId: id,
        companyId: quote.company_id,
        kind: "revision_created",
        summary: `Ny revisjon opprettet: ${nextLabel}`,
        details: { new_quote_id: newQ.id, revision_label: nextLabel },
      });
      await logQuoteActivity({
        quoteId: newQ.id,
        companyId: quote.company_id,
        kind: "created",
        summary: `Opprettet som revisjon ${nextLabel} av ${quote.quote_number}`,
      });
      return newQ.id as string;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote-children", id] });
      toast.success("Ny revisjon opprettet");
      navigate({ to: "/quotes/$id", params: { id: newId } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicate = useMutation({
    mutationFn: async () => {
      if (!quote) throw new Error("Tilbudet er ikke lastet");
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      const uidLocal = user.user.id;

      // Nytt tilbudsnummer
      const { data: s } = await supabase.from("settings").select("next_quote_seq,quote_year").eq("user_id", uidLocal).maybeSingle();
      const year = new Date().getFullYear();
      const seq = s?.quote_year === year ? (s?.next_quote_seq ?? 1) : 1;
      const number = `${year}-${String(seq).padStart(3, "0")}`;
      await supabase.from("settings").update({ next_quote_seq: seq + 1, quote_year: year }).eq("user_id", uidLocal);

      const { data: newQ, error: qErr } = await supabase.from("quotes").insert({
        user_id: uidLocal,
        company_id: quote.company_id,
        module_key: (quote.module_key ?? "electrician") as "electrician",
        quote_number: number,
        title: `${quote.title || "Tilbud"} (kopi)`,
        description: quote.description,
        customer_id: quote.customer_id,
        status: "draft",
        valid_until: quote.valid_until,
        vat_percent: Number(quote.vat_percent),
        discount_percent: Number(quote.discount_percent),
        notes: quote.notes,
        project_type: quote.project_type,
        material_display_mode: quote.material_display_mode,
      }).select().single();
      if (qErr) throw qErr;

      if (items.length > 0) {
        const payload = items.map((it, i) => ({
          quote_id: newQ.id,
          user_id: uidLocal,
          item_type: it.item_type,
          material_id: it.material_id,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_cost: Number(it.unit_cost),
          unit_price: Number(it.unit_price),
          total_cost: Number(it.quantity) * Number(it.unit_cost),
          total_price: Number(it.quantity) * Number(it.unit_price) * (it.item_type === "discount" ? -1 : 1),
          is_hidden_on_quote: it.is_hidden_on_quote,
          sort_order: i,
          internal_note: it.internal_note,
          customer_note: it.customer_note,
          labor_rate_id: it.labor_rate_id,
        }));
        await supabase.from("quote_items").insert(payload as never);
      }

      await logQuoteActivity({
        quoteId: id,
        companyId: quote.company_id,
        kind: "duplicated",
        summary: `Tilbud duplisert → ${number}`,
        details: { new_quote_id: newQ.id },
      });
      await logQuoteActivity({
        quoteId: newQ.id,
        companyId: quote.company_id,
        kind: "created",
        summary: `Opprettet som kopi av ${quote.quote_number}`,
      });
      return newQ.id as string;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success("Tilbud duplisert");
      navigate({ to: "/quotes/$id", params: { id: newId } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * Uttrykkelig registrering av kundens svar på et sendt tilbud.
   * Skjer aldri automatisk – kun når brukeren trykker.
   */
  const registerOutcome = useMutation({
    mutationFn: async (to: QuoteStatus) => {
      if (!quote) throw new Error("Ingen tilbud lastet");
      assertTransition(quote.status, to);
      const patch: TablesUpdate<"quotes"> = { status: to };
      const stampCol = statusTimestampColumn(to);
      if (stampCol) (patch as Record<string, string>)[stampCol] = new Date().toISOString();
      if (to === "approved" && !quote.project_number) {
        const { data: user } = await supabase.auth.getUser();
        if (user.user) {
          const { data: s } = await supabase
            .from("settings")
            .select("next_project_seq,quote_year")
            .eq("user_id", user.user.id)
            .maybeSingle();
          const year = new Date().getFullYear();
          const seq = s?.quote_year === year ? (s?.next_project_seq ?? 1) : 1;
          patch.project_number = `P${year}-${String(seq).padStart(3, "0")}`;
          await supabase
            .from("settings")
            .update({ next_project_seq: seq + 1 })
            .eq("user_id", user.user.id);
        }
      }
      const { error } = await supabase.from("quotes").update(patch).eq("id", quote.id);
      if (error) throw error;
      await logQuoteActivity({
        quoteId: quote.id,
        companyId: quote.company_id,
        kind: "status_changed" as QuoteActivityKind,
        summary: to === "approved" ? "Kunden godkjente tilbudet" : "Kunden avslo tilbudet",
      });
    },
    onSuccess: (_d, to) => {
      qc.invalidateQueries({ queryKey: ["quote", id] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote-activity", id] });
      toast.success(to === "approved" ? "Godkjenning registrert" : "Avslag registrert");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (childMatches.length > 0) return <Outlet />;

  if (!quote) return <div className="py-20 text-center text-sm text-muted-foreground">Laster…</div>;

  const dbColor = marginColorClass(totals.marginPct);
  const marginLevel = evaluateMargin(totals.marginPct);
  const numDisplay = quote.project_number
    ? `${quote.project_number} · ${quote.quote_number}${quote.revision_label ? ` Rev ${quote.revision_label}` : ""}`
    : `${quote.quote_number}${quote.revision_label ? ` Rev ${quote.revision_label}` : ""}`;

  return (
    <div className={readOnly ? "verqo-readonly" : undefined}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate({ to: "/quotes" })} className="btn btn-ghost -ml-2">
          <ArrowLeft className="h-4 w-4" /> Tilbake
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <SaveStatePill state={saveState} dirty={dirty} lastSavedAt={lastSavedAt} readOnly={readOnly} />
          <div className="inline-flex overflow-hidden rounded-md border border-border text-xs">
            <button onClick={() => setMode("calc")} className={`px-3 py-1.5 ${mode === "calc" ? "bg-foreground text-background" : "bg-transparent"}`}>Kalkyle</button>
            <button onClick={() => setMode("quote")} className={`px-3 py-1.5 ${mode === "quote" ? "bg-foreground text-background" : "bg-transparent"}`}>Tilbud</button>
          </div>
          <button
            onClick={() => { if (confirm("Duplisere dette tilbudet? Kopien blir en ny kladd med samme kunde og linjer.")) duplicate.mutate(); }}
            disabled={duplicate.isPending}
            className="btn btn-ghost"
            title="Dupliser tilbud"
          >
            <Copy className="h-4 w-4" /> Dupliser
          </button>
          <button onClick={() => setCopyOpen(true)} className="btn btn-ghost" title="Kopier til ny kunde">
            <Copy className="h-4 w-4" /> Til ny kunde
          </button>
          <button onClick={() => setTemplateOpen(true)} className="btn btn-ghost" title="Maler">
            <LayoutTemplate className="h-4 w-4" /> Maler
          </button>
          <button
            onClick={() => { if (confirm("Opprette ny revisjon? Gjeldende tilbud beholdes uendret.")) revise.mutate(); }}
            disabled={revise.isPending || isSupersededRevision}
            className="btn btn-ghost"
            title="Opprett ny revisjon"
          >
            <GitBranch className="h-4 w-4" /> Revisjon
          </button>
          <Link to="/quotes/$id/pdf" params={{ id }} target="_blank" className="btn btn-secondary">
            <Printer className="h-4 w-4" /> PDF
          </Link>
          {canInvoice && (
            <button
              onClick={() => setInvoiceOpen(true)}
              className="btn btn-primary"
              title="Lag faktura fra dette tilbudet"
            >
              <ReceiptText className="h-4 w-4" /> Lag faktura
            </button>
          )}
          <button
            onClick={() => setSendOpen(true)}
            disabled={readOnly || !quote}
            className="btn btn-primary"
            title="Send tilbud til kunde"
          >
            <Send className="h-4 w-4" /> Send
          </button>
          {dirty && !readOnly && (
            <button
              onClick={() => { if (confirm("Forkaste ulagrede endringer og hente lagret versjon?")) discardLocalChanges(); }}
              className="btn btn-ghost"
              title="Forkast endringer"
            >
              Forkast
            </button>
          )}
          <button onClick={() => save.mutate({})} disabled={save.isPending || !dirty || readOnly} className="btn btn-ghost">
            <Save className="h-4 w-4" /> {dirty ? "Lagre" : "Lagret"}
          </button>
        </div>
      </div>

      {quote && canInvoice && companyId && (
        <CreateInvoiceDialog
          open={invoiceOpen}
          onOpenChange={setInvoiceOpen}
          companyId={companyId}
          moduleKey={activeModuleKey}
          lockedQuote={{
            id: quote.id,
            label: `${quote.quote_number}${quote.title ? ` · ${quote.title}` : ""}`,
            status: quote.status,
          }}
        />
      )}

      {quote && (
        <SendQuoteDialog
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          quoteId={quote.id}
          quoteNumber={quote.quote_number}
          defaultEmail={((data?.customers ?? []).find((c: any) => c.id === quote.customer_id) as any)?.email ?? null}
          shareToken={(quote as any).share_token ?? null}
          shareEnabled={Boolean((quote as any).share_enabled)}
          status={quote.status}
          onSent={() => { qc.invalidateQueries({ queryKey: ["quote", id] }); qc.invalidateQueries({ queryKey: ["quote-activity", id] }); }}
        />
      )}

      {isLocked && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/10 p-4">
          <div className="flex items-start gap-2 text-sm">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <b>Sendt tilbud – kan ikke endres.</b>{" "}
              Dokumentet er sendt til kunde og er låst. Endringer må gjøres i en ny revisjon.
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            {canInvoice && (
              <button
                onClick={() => setInvoiceOpen(true)}
                className="btn btn-primary w-full sm:w-auto"
                title="Lag faktura fra dette tilbudet"
              >
                <ReceiptText className="h-4 w-4" /> Lag faktura
              </button>
            )}
            <button
              onClick={() => revise.mutate()}
              disabled={revise.isPending || isSupersededRevision}
              className="btn btn-secondary w-full sm:w-auto"
            >
              <GitBranch className="h-4 w-4" /> Lag ny revisjon
            </button>
          </div>
        </div>
      )}


      {isSupersededRevision && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <b>Skrivebeskyttet.</b> En nyere revisjon finnes ({(childRevisions ?? []).map((c) => c.revision_label ?? "?").join(", ")}).
            Åpne den nyeste for å redigere, eller opprett en ny revisjon fra den.
          </div>
        </div>
      )}


      {readySendErrors.length > 0 && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <div className="mb-1 flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> Tilbudet er ikke klart til sending
          </div>
          <ul className="list-inside list-disc space-y-0.5 pl-1">
            {readySendErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <PageHeader title={quote.title || "Uten tittel"} subtitle={numDisplay} />

      {/* Neste steg: kunde, jobb og status med én tydelig hovedhandling. */}
      {(() => {
        const customerName =
          (data?.customers ?? []).find((c) => c.id === quote.customer_id)?.name ?? "Ingen kunde";
        const st = quote.status as QuoteStatus;
        const next = isSupersededRevision ? null : nextQuoteAction(st, items.length > 0);
        const run = () => {
          if (!next) return;
          if (next.key === "continue_calc") setMode("calc");
          else if (next.key === "review_quote") setMode("quote");
          else if (next.key === "send") setSendOpen(true);
          else if (next.key === "create_invoice") setInvoiceOpen(true);
        };
        return (
          <section className="card mb-4 flex flex-wrap items-center gap-3 p-4">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{customerName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {quote.title || "Uten tittel"}
              </div>
              <span className={`chip mt-1 ${QUOTE_STATUS_CLASSES[st] ?? QUOTE_STATUS_CLASSES.draft}`}>
                {QUOTE_STATUS_LABELS[st] ?? st}
              </span>
            </div>
            {next?.key === "register_approval" ? (
              <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                <button
                  className="btn btn-primary w-full sm:w-auto"
                  disabled={registerOutcome.isPending}
                  onClick={() => {
                    if (confirm("Registrere at kunden har godkjent tilbudet?"))
                      registerOutcome.mutate("approved");
                  }}
                >
                  <CheckCircle2 className="h-4 w-4" /> Registrer godkjenning
                </button>
                <button
                  className="btn btn-ghost w-full sm:w-auto"
                  disabled={registerOutcome.isPending}
                  onClick={() => {
                    if (confirm("Registrere at kunden har avslått tilbudet?"))
                      registerOutcome.mutate("rejected");
                  }}
                >
                  <XCircle className="h-4 w-4" /> Registrer avslag
                </button>
              </div>
            ) : next ? (
              <button className="btn btn-primary w-full sm:w-auto" onClick={run}>
                {next.label}
              </button>
            ) : null}
          </section>
        );
      })()}


      {mode === "calc" && (
        <>
          <section className="card mb-4 grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
            <Stat label="Materialkost" value={formatCurrency(totals.material_cost)} />
            <Stat label="Salgsverdi materiell" value={formatCurrency(totals.material_price)} />
            <Stat label="DB (kr)" value={formatCurrency(totals.margin)} />
            <Stat label="DB %" value={`${totals.marginPct.toFixed(1)}%`} className={dbColor} />
          </section>
          {marginLevel === "low" && items.length > 0 && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <b>Lav dekningsgrad ({totals.marginPct.toFixed(1)} %)</b> — under anbefalt grense på 20 %.
                Vurder å øke priser eller redusere kostnader før tilbudet sendes.
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="card p-5">
            <h3 className="mb-4 font-semibold">Detaljer</h3>
            <div className="space-y-3">
              <div>
                <label className="label-base">Tittel</label>
                <input className="input-base" value={quote.title} onChange={(e) => updateQuote("title", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-base">Kunde</label>
                  <select className="input-base" value={quote.customer_id ?? ""} onChange={(e) => updateQuote("customer_id", e.target.value || null)}>
                    <option value="">— Ingen —</option>
                    {data?.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-base">Prosjekttype</label>
                  <select className="input-base" value={quote.project_type} onChange={(e) => updateQuote("project_type", e.target.value as ProjectType)}>
                    {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-base">Beskrivelse</label>
                <textarea className="input-base" rows={2} value={quote.description ?? ""} onChange={(e) => updateQuote("description", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-base">Status</label>
                  <select className="input-base" value={quote.status} onChange={(e) => updateQuote("status", e.target.value as QuoteStatus)}>
                    {(() => {
                      const current = quote.status;
                      const nextAllowed = new Set<QuoteStatus>([current, ...allowedNextStatuses(current)]);
                      return STATUS_KEYS.filter((k) => nextAllowed.has(k)).map((k) => (
                        <option key={k} value={k}>{QUOTE_STATUS_LABELS[k]}</option>
                      ));
                    })()}
                  </select>
                </div>
                <div>
                  <label className="label-base">Gyldig til</label>
                  <input type="date" className="input-base" value={quote.valid_until ?? ""} onChange={(e) => updateQuote("valid_until", e.target.value || null)} />
                </div>
              </div>
            </div>
          </section>

          <QuoteTermsSection
            companyId={quote.company_id}
            values={{
              terms_included: quote.terms_included,
              terms_excluded: quote.terms_excluded,
              terms_reservations: quote.terms_reservations,
              terms_payment: quote.terms_payment,
            }}
            onChange={(k, v) => updateQuote(k as keyof Quote, v as never)}
          />



          <section className="card p-5">
            <h3 className="mb-1 font-semibold">PDF-innstillinger</h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Overstyrer bedriftens standardvalg for dette tilbudet.
            </p>
            <div className="space-y-4">
              <div>
                <label className="label-base">Sammendrag til kunde</label>
                <textarea
                  className="input-base"
                  rows={3}
                  value={quote.customer_summary ?? ""}
                  onChange={(e) => updateQuote("customer_summary", e.target.value || null)}
                  placeholder="Kort beskrivelse av arbeidet — vises øverst i tilbudet."
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={quote.use_cover_page ?? false}
                    onChange={(e) => updateQuote("use_cover_page", e.target.checked)}
                  />
                  <span>Bruk forside</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={quote.include_images}
                    onChange={(e) => updateQuote("include_images", e.target.checked)}
                  />
                  <span>Inkluder bilder fra befaring</span>
                </label>
              </div>
              {quote.include_images && projectImages && projectImages.length > 0 && (
                <div>
                  <div className="mb-2 text-xs text-muted-foreground">
                    Velg bilder som skal legges ved (ingen valgt = alle vises).
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {projectImages.map((img) => {
                      const selectedIds = quote.included_image_ids ?? [];
                      const isSelected = selectedIds.length === 0 || selectedIds.includes(img.id);
                      return (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() => {
                            const current = quote.included_image_ids ?? [];
                            let next: string[];
                            if (current.length === 0) {
                              // starter fra "alle" → fjern denne
                              next = projectImages.filter((p) => p.id !== img.id).map((p) => p.id);
                            } else if (current.includes(img.id)) {
                              next = current.filter((x) => x !== img.id);
                            } else {
                              next = [...current, img.id];
                            }
                            updateQuote("included_image_ids", next);
                          }}
                          className={`relative aspect-square overflow-hidden rounded-md border-2 transition ${
                            isSelected ? "border-primary" : "border-transparent opacity-50"
                          }`}
                        >
                          <img src={img.url} alt="" className="h-full w-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {quote.include_images && projectImages && projectImages.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                  Ingen bilder tilknyttet prosjektet ennå.
                </div>
              )}
            </div>
          </section>


          {mode === "calc" && (
            <QuoteSectionsPanel
              quoteId={id}
              companyId={quote.company_id}
              userId={uid}
              sections={sections}
              lineCounts={lineCounts}
              readOnly={readOnly}
              onSectionRemoved={(sid) =>
                setItems((arr) => arr.map((it) => (it.section_id === sid ? { ...it, section_id: null } : it)))
              }
            />
          )}

          <section className="card p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="font-semibold">{mode === "calc" ? "Kalkyle" : "Tilbud – kundevisning"}</h3>
              {mode === "calc" && (
                <div className="flex flex-wrap items-center gap-1">
                  <AddButton icon={Package} label="Materiell" onClick={() => addItem("material")} />
                  <AddButton icon={Wrench} label="Smådeler" onClick={() => addItem("small_parts")} />
                  <AddButton icon={Clock} label="Timer" onClick={() => addItem("labor")} />
                  <AddButton icon={Truck} label="Servicebil" onClick={() => addItem("service_vehicle")} />
                  <AddButton icon={FileText} label="Dok." onClick={() => addItem("documentation")} />
                  <AddButton icon={Sparkles} label="Tilvalg" onClick={() => addItem("option")} />
                  <AddButton icon={Percent} label="Rabatt" onClick={() => addItem("discount")} />
                  <AddButton icon={Zap} label="Standardjobb" onClick={() => setStandardJobOpen(true)} />
                </div>
              )}
            </div>

            {mode === "quote" && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                <label className="text-muted-foreground">Kunden ser:</label>
                <select
                  className="input-base max-w-[16rem]"
                  value={quote.presentation ?? "itemized"}
                  onChange={(e) => updateQuote("presentation", e.target.value as "fixed_price" | "itemized")}
                >
                  <option value="itemized">Spesifisert – linje for linje</option>
                  <option value="fixed_price">Fastpris – kun sum per post</option>
                </select>
                <label className="text-muted-foreground">Vis materiell som:</label>
                <select
                  className="input-base max-w-[16rem]"
                  value={quote.material_display_mode}
                  onChange={(e) => updateQuote("material_display_mode", e.target.value as MaterialDisplayMode)}
                >
                  {(Object.keys(DISPLAY_MODE_LABELS) as MaterialDisplayMode[]).map((m) => (
                    <option key={m} value={m}>{DISPLAY_MODE_LABELS[m]}</option>
                  ))}
                </select>
              </div>
            )}

            {mode === "calc" && canSeeInternal && (
              <label className="mb-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showInternalPref}
                  onChange={(e) => setShowInternalPref(e.target.checked)}
                />
                Vis internpriser
              </label>
            )}

            {mode === "calc" && data && (
              <div className="mb-3">
                <MaterialSearch materials={data.materials} categories={data.categories} onPick={addMaterial} />
              </div>
            )}


            {visibleItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
                {mode === "calc" ? "Ingen linjer ennå. Legg til materiell, timer eller andre poster." : "Ingenting å vise kunden."}
              </div>
            ) : mode === "quote" ? (
              <div className="divide-y divide-border rounded-md border border-border">
                {visibleItems.map(({ it, key }) => {
                  const sum = Number(it.quantity) * Number(it.unit_price) * (it.item_type === "discount" ? -1 : 1);
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{it.description}</div>
                        {"customer_note" in it && it.customer_note && (
                          <div className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{it.customer_note}</div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatQuantity(it.quantity, it.unit)} {it.unit} × {formatCurrency(it.unit_price)}
                        </div>
                      </div>
                      <div className="font-medium">{formatCurrency(sum)}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full min-w-[860px] text-sm">
                  <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left font-medium">Linje</th>
                      {showInternal && <th className="p-2 text-right font-medium">Veil.</th>}
                      {showInternal && <th className="p-2 text-right font-medium">Rabatt</th>}
                      {showInternal && <th className="p-2 text-right font-medium">Vår pris</th>}
                      <th className="p-2 text-right font-medium">Kundepris</th>
                      <th className="p-2 text-right font-medium">Antall</th>
                      <th className="p-2 text-right font-medium">Sum salg</th>
                      {showInternal && <th className="p-2 text-right font-medium">DB</th>}
                      {showInternal && <th className="p-2 text-right font-medium">DG</th>}
                      <th className="w-24 p-2" />
                    </tr>
                  </thead>
                  {lineGroups.map((g) => (
                  <tbody
                    key={g.key}
                    onDragOver={(e) => { if (dragIdx != null) e.preventDefault(); }}
                    onDrop={() => { if (dragIdx != null) { assignSection(dragIdx, g.sectionId); setDragIdx(null); } }}
                  >
                    <tr className="border-b border-border bg-muted/30">
                      <td colSpan={showInternal ? 10 : 6} className="px-2 py-1.5 text-xs font-semibold">
                        {g.title}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {g.indices.length} linjer · {formatCurrency(g.sum)}
                          {dragIdx != null ? " · slipp her for å flytte linja hit" : ""}
                        </span>
                      </td>
                    </tr>
                    {g.indices.length === 0 && (
                      <tr className="border-b border-border">
                        <td colSpan={showInternal ? 10 : 6} className="px-2 py-2 text-xs text-muted-foreground">
                          Ingen linjer i denne posten.
                        </td>
                      </tr>
                    )}
                    {g.indices.map((i, posInGroup) => {
                      const it = items[i];
                      const notesOpen = !!openNotes[i];
                      const r = calcRows[i];
                      const colSpan = showInternal ? 10 : 6;
                      return (
                        <Fragment key={i}>
                          <tr
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragEnd={() => setDragIdx(null)}
                            className={`border-b border-border align-top ${r?.overridden ? "bg-warning/10" : ""} ${dragIdx === i ? "opacity-50" : ""}`}
                          >
                            <td className="min-w-[260px] p-2">
                              <div className="mb-1 flex items-center gap-2">
                                <span className="chip">{TYPE_LABEL[it.item_type]}</span>
                                {r?.overridden && (
                                  <span
                                    className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] text-warning"
                                    title={`Avviker fra kundepris i materiellregisteret (${formatCurrency(r.registryPrice ?? 0)}). Varen endres ikke.`}
                                  >
                                    Overstyrt pris
                                  </span>
                                )}
                              </div>
                              {(it.item_type === "material" || it.item_type === "small_parts") && data ? (
                                <MaterialAutocompleteInput
                                  value={it.description}
                                  materials={data.materials}
                                  categories={data.categories}
                                  onChangeText={(v) => updateItem(i, { description: v, material_id: null })}
                                  onPick={(mid) => applyMaterialToLine(i, mid)}
                                />
                              ) : (
                                <input className="input-base mb-1" placeholder="Beskrivelse" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
                              )}
                              {it.item_type === "labor" && data && (
                                <select className="input-base mt-1 text-xs" value={it.labor_rate_id ?? ""} onChange={(e) => changeLaborRate(i, e.target.value)}>
                                  <option value="">— Velg timekategori —</option>
                                  {data.laborRates.map((lrx) => <option key={lrx.id} value={lrx.id}>{lrx.name}</option>)}
                                </select>
                              )}
                              <input className="input-base mt-1 text-xs" value={it.unit} onChange={(e) => updateItem(i, { unit: e.target.value })} placeholder="Enhet" />
                            </td>
                            {showInternal && (
                              <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCurrency(r?.veil ?? 0)}</td>
                            )}
                            {showInternal && (
                              <td className="p-2 text-right tabular-nums text-muted-foreground">
                                {r?.discountPct == null ? "—" : `${r.discountPct.toFixed(1).replace(".", ",")} %`}
                              </td>
                            )}
                            {showInternal && (
                              <td className="p-2 text-right">
                                <input
                                  type="number"
                                  className="input-base h-9 w-24 text-right"
                                  value={it.unit_cost}
                                  onChange={(e) => updateItem(i, { unit_cost: Number(e.target.value) })}
                                />
                              </td>
                            )}
                            <td className="p-2 text-right">
                              <input
                                type="number"
                                className="input-base h-9 w-28 text-right"
                                value={it.unit_price}
                                onChange={(e) => updateItem(i, { unit_price: Number(e.target.value) })}
                                title="Kundepris på denne linjen. Endrer ikke varen i materiellregisteret."
                              />
                            </td>
                            <td className="p-2 text-right">
                              <input
                                type="number"
                                step={it.item_type === "labor" ? 0.5 : 1}
                                min={0}
                                className="input-base h-9 w-20 text-right"
                                value={it.quantity}
                                onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
                              />
                            </td>
                            <td className="p-2 text-right font-medium tabular-nums">{formatCurrency(r?.sumSale ?? 0)}</td>
                            {showInternal && (
                              <td className="p-2 text-right tabular-nums">{formatCurrency(r?.db ?? 0)}</td>
                            )}
                            {showInternal && (
                              <td className={`p-2 text-right tabular-nums ${dgClass(r?.dg ?? null)}`}>
                                {r && r.cost === 0 && r.price > 0 ? (
                                  <button
                                    type="button"
                                    className="text-xs underline decoration-dotted underline-offset-2 text-warning"
                                    onClick={() => setCostPickIdx(i)}
                                    title="Hent innkjøpspris fra materiellregisteret"
                                  >
                                    Kost mangler
                                  </button>
                                ) : r?.dg == null ? (
                                  "—"
                                ) : (
                                  `${r.dg.toFixed(1).replace(".", ",")} %`
                                )}
                              </td>
                            )}

                            <td className="p-2">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setOpenNotes((o) => ({ ...o, [i]: !notesOpen }))}
                                  className={`btn btn-ghost p-1.5 text-xs ${it.internal_note || it.customer_note ? "text-primary" : ""}`}
                                  title="Notater"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => updateItem(i, { is_hidden_on_quote: !it.is_hidden_on_quote })}
                                  className={`btn btn-ghost p-1.5 text-xs ${it.is_hidden_on_quote ? "text-warning" : ""}`}
                                  title={it.is_hidden_on_quote ? "Skjult på tilbud" : "Vises på tilbud"}
                                >
                                  {it.is_hidden_on_quote ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                                <button
                                  onClick={() => moveLineTo(i, g.indices[posInGroup - 1])}
                                  disabled={posInGroup === 0}
                                  className="btn btn-ghost p-1.5"
                                  title="Flytt opp"
                                >
                                  <ArrowUp className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => moveLineTo(i, g.indices[posInGroup + 1])}
                                  disabled={posInGroup === g.indices.length - 1}
                                  className="btn btn-ghost p-1.5"
                                  title="Flytt ned"
                                >
                                  <ArrowDown className="h-4 w-4" />
                                </button>
                                <button onClick={() => removeItem(i)} className="btn btn-ghost p-1.5 text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              {sections.length > 0 && (
                                <select
                                  className="input-base mt-1 h-8 text-xs"
                                  value={it.section_id ?? ""}
                                  onChange={(e) => assignSection(i, e.target.value || null)}
                                  title="Flytt linja til en annen post"
                                >
                                  <option value="">Diverse</option>
                                  {sections.map((s) => (
                                    <option key={s.id} value={s.id}>{s.title}</option>
                                  ))}
                                </select>
                              )}
                            </td>
                          </tr>
                          {notesOpen && (
                            <tr className="border-b border-border bg-muted/20">
                              <td colSpan={colSpan} className="p-2">
                                <div className="grid gap-2 sm:grid-cols-2">
                                  <div>
                                    <label className="label-base">Internt notat</label>
                                    <textarea className="input-base" rows={2} value={it.internal_note ?? ""} onChange={(e) => updateItem(i, { internal_note: e.target.value })} placeholder="Vises kun i kalkyle" />
                                  </div>
                                  <div>
                                    <label className="label-base">Notat til kunde</label>
                                    <textarea className="input-base" rows={2} value={it.customer_note ?? ""} onChange={(e) => updateItem(i, { customer_note: e.target.value })} placeholder="Vises på tilbud" />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  ))}
                </table>
              </div>

            )}
          </section>

          {mode === "calc" && (
            <section className="card p-5">
              <h3 className="mb-3 font-semibold">Notater / vilkår</h3>
              <textarea className="input-base" rows={3} value={quote.notes ?? ""} onChange={(e) => updateQuote("notes", e.target.value)} placeholder="Vilkår, forbehold, m.m." />
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-5">
            <h3 className="mb-3 font-semibold">Summering</h3>
            {mode === "calc" && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="label-base">Rabatt %</label>
                  <input type="number" step="0.1" className="input-base" value={quote.discount_percent} onChange={(e) => updateQuote("discount_percent", Number(e.target.value))} />
                </div>
                <div>
                  <label className="label-base">MVA %</label>
                  <input type="number" step="0.1" className="input-base" value={quote.vat_percent} onChange={(e) => updateQuote("vat_percent", Number(e.target.value))} />
                </div>
              </div>
            )}
            <div className="mt-4 space-y-1.5 text-sm">
              {mode === "calc" && showInternal && (
                <>
                  <Row label="Sum veiledende" value={formatCurrency(bolk4.sumVeil)} muted />
                  <Row label="Sum kost" value={formatCurrency(bolk4.sumCost)} muted />
                  <Row label="Sum rabatt oppnådd" value={formatCurrency(bolk4.sumDiscountAchieved)} muted />
                  <div className="my-2 border-t border-border" />
                </>
              )}
              <Row label="Sum kundepris" value={formatCurrency(bolk4.sumSale)} />
              {mode === "calc" && showInternal && (
                <>
                  <Row label="DB" value={formatCurrency(bolk4.db)} />
                  <div className="flex justify-between">
                    <span>DG</span>
                    <span className={`font-semibold ${dgClass(bolk4.dg)}`}>
                      {bolk4.dg == null ? "—" : `${bolk4.dg.toFixed(1).replace(".", ",")} %`}
                    </span>
                  </div>
                </>
              )}
              <Row label="Sum arbeid" value={formatCurrency(bolk4.sumLabor)} muted />
              <div className="my-2 border-t border-border" />
              {Number(quote.discount_percent) > 0 && (
                <Row label={`Rabatt (${quote.discount_percent}%)`} value={`- ${formatCurrency(bolk4.sumSale * Number(quote.discount_percent) / 100)}`} muted />
              )}
              <Row label="Totalt eks. mva" value={formatCurrency(totals.subtotal_price)} />
              <Row label={`MVA (${quote.vat_percent} %)`} value={formatCurrency(totals.vat)} />
              <Row label="Totalt inkl. mva" value={formatCurrency(totals.total)} strong />
            </div>

          </div>

          <div className="card mt-4 p-5">
            <h3 className="mb-3 flex items-center gap-2 font-semibold">
              <History className="h-4 w-4" /> Aktivitet
            </h3>
            {(activity ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">Ingen aktivitet ennå.</p>
            ) : (
              <ol className="space-y-2 text-xs">
                {(activity ?? []).map((a) => (
                  <li key={a.id} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{a.summary}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString("nb-NO")}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </aside>
      </div>
      {standardJobOpen && uid && (
        <AddStandardJobModal
          userId={uid}
          moduleKey={(quote.module_key ?? "electrician") as never}
          onClose={() => setStandardJobOpen(false)}
          onApply={applyStandardJob}
        />
      )}
      <CopyQuoteDialog open={copyOpen} onClose={() => setCopyOpen(false)} quoteId={id} />
      <QuoteTemplateDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        companyId={quote.company_id}
        moduleKey={quote.module_key ?? "electrician"}
        lines={items.map((it) => ({
          item_type: it.item_type,
          material_id: it.material_id,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          labor_rate_id: it.labor_rate_id,
        }))}
        onApply={applyTemplateLines}
      />
      {costPickIdx != null && (
        <RegistryMaterialPicker
          open
          onClose={() => setCostPickIdx(null)}
          moduleKey={(quote.module_key ?? "electrician") as ModuleKey}
          initialSearch={items[costPickIdx]?.description ?? ""}
          showCost={canSeeInternal}
          onPick={(m) => {
            applyCostFromRegistry(costPickIdx, m);
            setCostPickIdx(null);
          }}
        />
      )}

    </div>
  );
}

function MaterialSearch({ materials, categories, onPick }: { materials: MaterialLite[]; categories: CategoryLite[]; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const catName = (id: string | null) => categories.find((c) => c.id === id)?.name ?? null;

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const sorted = [...materials].sort((a, b) => Number(b.is_favorite) - Number(a.is_favorite));
    if (!s) return sorted.slice(0, 10);
    return sorted
      .filter((m) => [m.description, m.item_number, m.supplier, m.manufacturer, catName(m.category_id)].some((v) => v?.toLowerCase().includes(s)))
      .slice(0, 12);
  }, [q, materials, categories]);

  function pick(id: string) { onPick(id); setQ(""); setActive(0); setOpen(false); }

  return (
    <div ref={boxRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        className="input-base pl-9 text-sm"
        placeholder="Søk materiell – favoritter først"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && results[active]) { e.preventDefault(); pick(results[active].id); }
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {results.map((m, i) => {
            const cn = catName(m.category_id);
            return (
              <button
                key={m.id}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(m.id)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${i === active ? "bg-muted" : ""}`}
              >
                {m.image_url ? (
                  <img src={m.image_url} alt="" loading="lazy" className="h-10 w-10 flex-none rounded border border-border object-contain bg-white" />
                ) : (
                  <div className="h-10 w-10 flex-none rounded border border-dashed border-border" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {m.is_favorite && <span className="chip text-warning">★</span>}
                    {m.item_number && <span className="text-xs font-mono text-muted-foreground">{m.item_number}</span>}
                    {cn && <span className="chip">{cn}</span>}
                    {m.is_small_part && <span className="chip"><EyeOff className="h-3 w-3" />Smådel</span>}
                  </div>
                  <div className="truncate">{m.description}</div>
                </div>
                <div className="whitespace-nowrap text-sm font-medium">{formatCurrency(Number(m.purchase_price))}<span className="text-xs text-muted-foreground">/{m.unit} innkj.</span></div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Autocomplete for beskrivelse-feltet på en materiell-/smådelslinje.
 * Fungerer som vanlig input, men foreslår treff fra materialregisteret mens du skriver.
 */
function MaterialAutocompleteInput({
  value, materials, categories, onChangeText, onPick,
}: {
  value: string;
  materials: MaterialLite[];
  categories: CategoryLite[];
  onChangeText: (v: string) => void;
  onPick: (materialId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const catName = (id: string | null) => categories.find((c) => c.id === id)?.name ?? null;

  const results = useMemo(() => {
    const s = value.trim().toLowerCase();
    if (!s) return [] as MaterialLite[];
    return [...materials]
      .sort((a, b) => Number(b.is_favorite) - Number(a.is_favorite))
      .filter((m) => [m.description, m.item_number, m.supplier, m.manufacturer, catName(m.category_id)].some((v) => v?.toLowerCase().includes(s)))
      .slice(0, 12);
  }, [value, materials, categories]);

  function pick(id: string) { onPick(id); setOpen(false); setActive(0); }

  return (
    <div ref={boxRef} className="relative mb-2">
      <input
        className="input-base"
        placeholder="Beskrivelse – skriv for å søke i materialregisteret"
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChangeText(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter") { e.preventDefault(); pick(results[active].id); }
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {results.map((m, i) => {
            const cn = catName(m.category_id);
            return (
              <button
                key={m.id}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(m.id)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${i === active ? "bg-muted" : ""}`}
              >
                {m.image_url ? (
                  <img src={m.image_url} alt="" loading="lazy" className="h-10 w-10 flex-none rounded border border-border object-contain bg-white" />
                ) : (
                  <div className="h-10 w-10 flex-none rounded border border-dashed border-border" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {m.is_favorite && <span className="chip text-warning">★</span>}
                    {m.item_number && <span className="text-xs font-mono text-muted-foreground">{m.item_number}</span>}
                    {cn && <span className="chip">{cn}</span>}
                    {m.is_small_part && <span className="chip"><EyeOff className="h-3 w-3" />Smådel</span>}
                  </div>
                  <div className="truncate">{m.description}</div>
                </div>
                <div className="whitespace-nowrap text-sm font-medium">{formatCurrency(Number(m.purchase_price))}<span className="text-xs text-muted-foreground">/{m.unit} innkj.</span></div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddButton({ icon: Icon, label, onClick }: { icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="btn btn-ghost px-2 py-1.5 text-xs">
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function NumberInput({ label, value, onChange, step = 0.01, min }: { label: string; value: number; onChange: (v: number) => void; step?: number; min?: number }) {
  const [text, setText] = useState<string>(() => formatNum(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(formatNum(value));
  }, [value]);
  return (
    <div>
      <label className="label-base">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        className="input-base"
        value={text}
        onFocus={(e) => { focused.current = true; e.currentTarget.select(); }}
        onBlur={() => {
          focused.current = false;
          const n = Number(text.replace(",", "."));
          const clean = Number.isFinite(n) ? n : 0;
          setText(formatNum(clean));
          if (clean !== value) onChange(clean);
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const n = Number(raw.replace(",", "."));
          if (raw !== "" && Number.isFinite(n) && n !== value) onChange(n);
        }}
      />
    </div>
  );
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "0";
  // Strip trailing zeros / avoid leading zeros like "02"
  return String(Number(n));
}

function Row({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span className={strong ? "text-base font-semibold" : "font-medium"}>{value}</span>
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${className ?? ""}`}>{value}</div>
    </div>
  );
}

function SaveStatePill({ state, dirty, lastSavedAt, readOnly }: { state: SaveState; dirty: boolean; lastSavedAt: Date | null; readOnly: boolean }) {
  if (readOnly) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] text-warning">
        <Lock className="h-3 w-3" /> Skrivebeskyttet
      </span>
    );
  }
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Lagrer…
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
        <XCircle className="h-3 w-3" /> Feil ved lagring
      </span>
    );
  }
  if (dirty) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground">
        Ulagrede endringer
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[11px] text-success">
      <CheckCircle2 className="h-3 w-3" /> Lagret{lastSavedAt ? ` ${lastSavedAt.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}` : ""}
    </span>
  );
}

/** Fargekode for dekningsgrad: under 15 % rød, 15–25 % gul, over 25 % grønn. */
function dgClass(dg: number | null): string {
  if (dg == null) return "";
  if (dg < 15) return "text-destructive";
  if (dg <= 25) return "text-warning";
  return "text-success";
}

/**
 * Bolk A4 – tekstblokker på tilbudsdokumentet.
 * Feltene fylles automatisk ved oppretting; knappen henter standardteksten
 * tilbake dersom man har endret noe og ønsker å nullstille feltet.
 */
function QuoteTermsSection({
  companyId,
  values,
  onChange,
}: {
  companyId: string;
  values: Record<string, string | null>;
  onChange: (key: string, value: string | null) => void;
}) {
  const { data: defaults } = useQuery({
    queryKey: ["company-quote-terms", companyId],
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: () => fetchCompanyQuoteTerms(companyId),
  });

  const hasAnyDefault = QUOTE_TERMS_FIELDS.some((f) => !!defaults?.[f.key]);

  return (
    <section className="card p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-semibold">Tilbudsdokument</h3>
        <button
          type="button"
          className="btn btn-ghost text-xs"
          disabled={!hasAnyDefault}
          onClick={() => {
            if (!defaults) return;
            for (const f of QUOTE_TERMS_FIELDS) onChange(f.key, defaults[f.key]);
            toast.success("Standardtekster hentet inn");
          }}
        >
          Sett inn standard i alle
        </button>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Fylles automatisk fra Innstillinger når tilbudet opprettes. Endringer her gjelder kun dette tilbudet.
        Tomme felter vises ikke i PDF-en.
      </p>
      <div className="space-y-4">
        {QUOTE_TERMS_FIELDS.map((f) => (
          <div key={f.key}>
            <div className="flex items-center justify-between gap-2">
              <label className="label-base">{f.label}</label>
              {defaults?.[f.key] && defaults[f.key] !== (values[f.key] ?? null) && (
                <button
                  type="button"
                  className="text-xs text-primary underline"
                  onClick={() => onChange(f.key, defaults[f.key])}
                >
                  Sett inn standard
                </button>
              )}
            </div>
            <textarea
              className="input-base"
              rows={3}
              value={values[f.key] ?? ""}
              onChange={(e) => onChange(f.key, e.target.value || null)}
              placeholder={f.placeholder}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
